import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  CheckCircle,
  XCircle,
  Loader2,
  PencilRuler,
  Ellipsis,
  ChevronUp,
  Filter,
  BrainCircuit,
  Bell,
  ListTodo,
} from 'lucide-react';
import {
  FiCheck,
  FiMail,
  FiVideo,
  FiPlus,
  FiChevronDown,
  FiX,
  FiArrowRight,
  FiClock,
} from 'react-icons/fi';
import { VscReport } from 'react-icons/vsc';
import { useNavigate } from 'react-router-dom';

import { DataState } from '../../context/DataProvider';
import TaskPlan from './TaskPlan';
import { useTaskPlan } from '../../context/TaskPlanProvider';
import { RECENT_GOAL_COUNT } from '../../utils/constants';
import { appImages } from '../../utils/constants';
import { useNotifications } from '../../context/NotificationProvider';
import { useChangeRequests } from '../../context/ChangeRequestProvider';
import { useEmails } from '../../context/EmailsProvider';
import NoMailScreen from '../dialog/NoMailScreen';
import EmailLabels from '../dialog/EmailLabels';
import {
  getGmailEmailDetailsFromDB,
  getOutlookEmailDetailsFromDB,
} from '../../services/api';

const TODOS_INIT = [
  {
    id: 1,
    text: 'Join Quick Meeting on Google Meet in 16 min',
    done: false,
    note: 'I have a Quick Meeting starting in about 15 minutes on Google Meet. Help me think through what I might want to cover or any quick talking points I should prep before jumping in.',
  },
  { id: 2, text: 'Do changes', done: false, note: 'kasjd' },
  { id: 3, text: 'Finalize investor deck slides', done: true, note: '' },
];

const SUGGESTIONS = [
  { id: 1, text: 'Reply to Razorpay on OAuth vs Custom Onboarding' },
  { id: 2, text: 'Decide on new Razorpay UPI credit instruments' },
  { id: 3, text: 'Reply to Akshay about Hyderabad investor meeting' },
];

const MEETINGS = [
  {
    id: 1,
    title: 'Technosport X SuperManager AGI',
    time: '10:00 AM – 10:30 AM',
    attendees: ['AN', 'AD', 'MA', 'NI', 'MZ'],
    colors: [
      'bg-violet-600',
      'bg-blue-600',
      'bg-emerald-600',
      'bg-amber-600',
      'bg-rose-600',
    ],
    summary:
      "First demo with Technosport's D2C Lead, Anil Pradhan — a technically sharp operator. Nail the cross-team coordination and analytics angles.",
    emails: [
      'anil@technosport.in',
      'aditya46660@gmail.com',
      'manrozeoqlousagi@gmail.com',
      'nirmalnambiar@oqlous.com',
      'manroze@trysupermanager.com',
    ],
    prep: {
      who: 'Anil Pradhan, D2C Lead at Technosport (since Sep 2024). Ex-Myntra Senior Manager CRM, ex-Udaan, ex-Apollo Pharmacy — 7+ years in digital growth, performance marketing, and retention. Technically strong: Python, SQL, data visualization.',
      about:
        "India's fast-growing performance activewear brand (think Indian Lululemon). Founded 2015, ~₹436 Cr revenue, Series A ($29.5M raised), 525+ employees. Just opened their 34th exclusive store.",
      fit: "Technosport's D2C operation means Anil's team juggles campaign launches, ecommerce ops, CRM workflows, logistics coordination — all across different tools. That cross-app chaos is exactly what SuperManager solves.",
      angles: [
        'Campaign/product launch coordination across teams (marketing, ops, logistics)',
        'Automated reporting and status updates instead of manual data pulls',
        'The Python/SQL guy will appreciate analytics and custom productivity charts',
        'Retail expansion coordination — 34 stores and growing means project management overhead',
      ],
      team: 'Manroze (COO, organizer) and Aditya Sharma from Oqlous/SuperManager are joining you.',
      flow: 'Brief intro → integrations list → live Jira/task demo → automated reporting → workflow builder → pricing. Ask about their current tool stack early.',
    },
  },
  {
    id: 2,
    title: 'The House of Rare X SuperManager AGI',
    time: '11:00 AM – 11:30 AM',
    attendees: ['PU', 'AD', 'RK'],
    colors: ['bg-pink-600', 'bg-blue-600', 'bg-teal-600'],
    summary:
      "Follow-up demo with House of Rare's ops team. Focus on inventory and vendor coordination workflows.",
  },
  {
    id: 3,
    title: 'Nirmal Nambiar and Tingyu Xiang',
    time: '12:30 PM – 12:40 PM',
    attendees: ['TI'],
    colors: ['bg-cyan-600'],
    summary: 'Quick sync on integration timeline and API access.',
  },
  {
    id: 4,
    title: 'Prince X Website',
    time: '3:00 PM – 4:00 PM',
    attendees: ['PR'],
    colors: ['bg-orange-600'],
    summary:
      'Website review and feedback session. Cover landing page, pricing section, and CTA copy.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Workspace tab helpers (from old code)
// ─────────────────────────────────────────────────────────────────────────────

function formatTimeAgo(dateInput) {
  if (!dateInput) return '';
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function generateMiscText(notification) {
  if (notification.source_type === 'change_request') {
    const cr = notification.cr_data;
    return `${cr?.classification || 'Scope change'} · ${cr?.phase || ''}`;
  }
  if (notification.source_type === 'listener') {
    const payload = notification.listener_data.payload;
    switch (notification.listener_data.app) {
      case 'jira':
        return `Ticket #${payload.ticketId}`;
      case 'gmail':
        return `From: ${payload.from}`;
      case 'zoho_crm':
        return `Lead: ${payload.leadName}, Value: $${payload.dealSize.toLocaleString()}`;
      case 'slack':
        return `From: ${payload.user} in #${payload.channel}`;
      default:
        return `Source: ${notification.listener_data.app}`;
    }
  }
  if (notification.source_type === 'workflow')
    return `Workflow: "${notification.workflow_data.workflow_title}"`;
  if (notification.source_type === 'meeting')
    return `Subject: ${notification.subject}`;
  return '';
}

const NotificationItem = ({ notification, onClick }) => {
  const isCR = notification.source_type === 'change_request';
  const app =
    notification.source_type === 'listener'
      ? notification.listener_data.app
      : notification.source_type === 'meeting'
        ? notification.app
        : 'agent';
  const miscText = generateMiscText(notification);
  return (
    <button
      onClick={onClick}
      className="flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/60"
    >
      {isCR ? (
        <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-amber-500/20 text-[10px] font-bold text-amber-400">
          CR
        </div>
      ) : (
        <img
          src={appImages[app]}
          alt={app}
          className="mt-0.5 h-5 w-5 flex-shrink-0 rounded"
        />
      )}
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <p className="truncate text-xs font-medium text-zinc-200">
            {notification.event_name}
          </p>
          <p className="ml-2 flex-shrink-0 text-[10px] text-zinc-500">
            {formatTimeAgo(notification.timestamp)}
          </p>
        </div>
        <p className="mt-0.5 truncate text-[11px] text-zinc-400">{miscText}</p>
      </div>
    </button>
  );
};

const WaitingEventItem = ({ event, onClick }) => {
  const tasks = event.llm_summary?.task_wise_summary || [];
  const jiraUpdateCount = tasks.reduce(
    (s, t) => s + (t.proposed_jira_issue_updates?.length || 0),
    0,
  );
  const actionCount = (event.llm_summary?.extracted_action_items || []).length;
  const blockerCount = (event.llm_summary?.unique_blockers || []).length;

  const chips = [
    jiraUpdateCount > 0 &&
      `${jiraUpdateCount} Jira update${jiraUpdateCount > 1 ? 's' : ''}`,
    actionCount > 0 && `${actionCount} action${actionCount > 1 ? 's' : ''}`,
    blockerCount > 0 && `${blockerCount} blocker${blockerCount > 1 ? 's' : ''}`,
  ].filter(Boolean);

  return (
    // eslint-disable-next-line
    <div
      onClick={onClick}
      className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-zinc-800/60"
    >
      <img
        src={appImages[event.app] || appImages.microsoft_teams}
        alt={event.app}
        className="mt-0.5 h-5 w-5 flex-shrink-0 rounded"
      />
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-zinc-200">
            {event.event_name || event.subject}
          </p>
          <p className="ml-2 flex-shrink-0 text-[10px] text-zinc-500">
            {formatTimeAgo(event.timestamp)}
          </p>
        </div>
        {chips.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {chips.map((chip) => (
              <span
                key={chip}
                className="rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-amber-400"
              >
                {chip}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-0.5 truncate text-[11px] text-zinc-400">
            Pending review
          </p>
        )}
      </div>
    </div>
  );
};

const WaitingForTask = () => (
  <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-zinc-500">
    <PencilRuler className="mb-4 h-10 w-10 text-zinc-600" strokeWidth={1.5} />
    <h4 className="mb-1 text-sm font-semibold text-zinc-400">
      Workspace Ready
    </h4>
    <p className="text-xs">
      Enter a task to see the agent's plan and actions here.
    </p>
  </div>
);

const ActionItem = ({ action, onActionClick }) => {
  let description = `Call ${action.function?.name}`;
  try {
    const args = JSON.parse(action.function?.arguments || '{}');
    description = args.action_description || description;
  } catch (e) {}

  const isRunning = action.status === 'running';
  const getStatusDetails = () => {
    if (isRunning) {
      switch (action.running_phase || 'calling_tool') {
        case 'filtering':
          return {
            icon: <Filter className="h-4 w-4 animate-pulse text-cyan-400" />,
            textColor: 'text-cyan-300',
          };
        case 'analyzing':
          return {
            icon: <BrainCircuit className="h-4 w-4 text-indigo-400" />,
            textColor: 'text-indigo-300',
          };
        default:
          return {
            icon: <Loader2 className="h-4 w-4 animate-spin text-blue-400" />,
            textColor: 'text-blue-300',
          };
      }
    }
    if (action.status === 'success')
      return {
        icon: <CheckCircle className="h-4 w-4 text-emerald-400" />,
        textColor: 'text-zinc-300',
      };
    if (action.status === 'failed')
      return {
        icon: <XCircle className="h-4 w-4 text-red-400" />,
        textColor: 'text-red-300',
      };
    return { icon: null, textColor: 'text-zinc-400' };
  };

  const { icon, textColor } = getStatusDetails();
  return (
    <button
      onClick={() => onActionClick(action.id)}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/60"
    >
      <div className="w-4 flex-shrink-0">{icon}</div>
      <p className={`flex-1 truncate text-xs font-medium ${textColor}`}>
        {description}
      </p>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Personal tab shared atoms
// ─────────────────────────────────────────────────────────────────────────────

function Av({ initials, color }) {
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${color}`}
    >
      {initials}
    </span>
  );
}

function Label({ children }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
      {children}
    </p>
  );
}

// ─── Prep Modal ───────────────────────────────────────────────────────────────

function PrepModal({ meeting, onClose }) {
  if (!meeting?.prep) return null;
  const { who, about, fit, angles, team, flow } = meeting.prep;
  const sections = [
    [
      "Who you're meeting",
      <p key="w">
        <span className="font-semibold text-zinc-100">Anil Pradhan</span>, {who}
      </p>,
    ],
    ['What Technosport actually is', <p key="a">{about}</p>],
    ['Why SuperManager AGI fits', <p key="f">{fit}</p>],
    [
      'Angles that should land',
      <ul key="ang" className="space-y-1.5">
        {angles.map((a, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
            {a}
          </li>
        ))}
      </ul>,
    ],
    ['Your team on the call', <p key="t">{team}</p>],
    ['Standard demo flow', <p key="fl">{flow}</p>],
  ];
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center rounded-xl bg-black/70 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[90%] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-5 py-3.5">
          <p className="text-sm font-semibold text-zinc-100">Meeting Prep</p>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="sc space-y-4 overflow-y-auto px-5 py-4 text-sm leading-relaxed text-zinc-300">
          {sections.map(([label, content]) => (
            <section key={label}>
              <Label>{label}</Label>
              <div className="mt-1.5">{content}</div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Email Panel (real data from useEmails) ───────────────────────────────────

// Priority badge helper — matches Dialog.jsx logic
function PriorityBadge({ priority }) {
  const isLow = priority <= 1;
  const isMed = priority <= 3;
  const label = isLow ? 'Low' : isMed ? 'Medium' : 'High';
  const color = isLow ? 'bg-[#5652CF]' : isMed ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold text-white ${color}`}
    >
      {label}
    </span>
  );
}

// Inline expanded detail — summary + CTA
function EmailDetail({ email, onClose, onChatWithAgent }) {
  const subject = email?.subject || '(no subject)';
  const from = email?.from?.name || email?.from || 'Unknown';
  const body = email?.snippet || email?.bodyPreview || '';

  // Real fields from the email analysis pipeline————————————————
  const aiSummary = email?.summary || null;
  const keyPoints = [
    ...(email?.criticalPoints || []),
    ...(email?.insights || []),
  ];
  const [showAll, setShowAll] = useState(false);
  const visiblePoints = showAll ? keyPoints : keyPoints.slice(0, 2);

  // Fallback: build a quick snippet summary if no AI summary yet
  const displaySummary =
    aiSummary ||
    `Email from ${from} — "${subject}". ${body.slice(0, 100)}${body.length > 100 ? '…' : ''}`;

  return (
    <div className="animate-fi mx-3 mb-3 overflow-hidden rounded-xl border border-zinc-700 bg-zinc-800/50">
      {/* ── AI Summary ─────────────────────────────────────────── */}
      <div className="space-y-2 px-3 pb-2 pt-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
          AI Summary
        </p>
        <p className="text-xs leading-relaxed text-zinc-300">
          {displaySummary}
        </p>

        {/* Key points */}
        {keyPoints.length > 0 && (
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              Key Points
            </p>
            <ul className="space-y-1 pl-3">
              {visiblePoints.map((pt, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[11px] leading-snug text-zinc-400"
                >
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-zinc-500" />
                  {pt}
                </li>
              ))}
            </ul>
            {keyPoints.length > 2 && (
              <button
                onClick={() => setShowAll((p) => !p)}
                className="mt-1 text-[10px] text-[#5652CF] hover:underline"
              >
                {showAll ? 'Show less' : `+${keyPoints.length - 2} more`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── actions ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1">
        <button
          onClick={() => onChatWithAgent(email)}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#5652CF] py-2 text-xs font-medium text-white transition-colors hover:bg-[#5652CF]/80"
        >
          <FiMail className="h-3.5 w-3.5" />
          Chat with Agent
        </button>
        <button
          onClick={onClose}
          className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-700 hover:text-zinc-200"
        >
          <FiX className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function EmailPanel({ onStageChat }) {
  // ── real data ──
  const {
    emails,
    currentPage,
    setCurrentPage,
    emailfilter,
    setEmailFilter,
    loadingEmails,
    totalPages,
    isOutlook,
    isGmailAuthenticated,
    isOutlookAuthenticated,
    isGmailSynchronized,
    isOutlookSynchronized,
    emailStats,
  } = useEmails();

  const { userGoogleId } = DataState();

  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({}); // message_id → full email detail
  const [filterOpen, setFilterOpen] = useState(false);
  const sortKey = 'date';
  const filterRef = useRef(null);

  // Fetch full email detail (with summary, criticalPoints, insights…) on expand
  const fetchDetail = async (email) => {
    const msgId = email?.message_id || email?.id;
    if (!msgId || detailCache[msgId]) return; // already cached
    try {
      const fn = isOutlook
        ? getOutlookEmailDetailsFromDB
        : getGmailEmailDetailsFromDB;
      const detail = await fn(msgId, userGoogleId);
      if (detail) setDetailCache((prev) => ({ ...prev, [msgId]: detail }));
    } catch (e) {
      toast.error('Failed to load email details.');
    }
  };

  // close filter on outside click
  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleFilterChange = (key, value) => {
    setEmailFilter((prev) => {
      const next = { ...prev };
      if (key === 'Inbox' || key === 'SentItems') {
        delete next[key === 'Inbox' ? 'SentItems' : 'Inbox'];
      }
      if (next[key] === value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const handleChatWithAgent = (email) => {
    if (!onStageChat) return;
    const appLabel = isOutlook ? 'microsoft_outlook' : 'gmail';
    const payload = {
      _synthetic: true, // not a real DB notification
      source_type: 'listener',
      event_name: email?.subject || 'Email',
      id: email?.id || String(Date.now()),
      listener_data: {
        app: appLabel,
        payload: {
          from: email?.from?.address || email?.from || '',
          to: email?.to || '',
          subject: email?.subject || '',
          body: email?.bodyPreview || email?.snippet || '',
        },
      },
    };
    onStageChat(payload, payload.id);
  };

  const nextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  // client-side sort only — EmailLabels drives filtering via setEmailFilter (server-side)
  const rawList = isOutlook
    ? (emails || []).map((e) => ({ ...e, snippet: e.bodyPreview }))
    : emails || [];

  const sorted = [...rawList].sort((a, b) => {
    if (sortKey === 'sender') {
      const na = a.from?.name || a.from || '';
      const nb = b.from?.name || b.from || '';
      return na.localeCompare(nb);
    }
    return new Date(b.date) - new Date(a.date);
  });

  const isConnected = isOutlook ? isOutlookAuthenticated : isGmailAuthenticated;
  const isSynced = isOutlook ? isOutlookSynchronized : isGmailSynchronized;

  // ── not connected ──
  if (!isConnected) {
    return (
      <div className="flex h-full w-full flex-1 flex-col gap-2">
        <div className="mx-auto flex h-full w-full flex-col items-center overflow-hidden ">
          <NoMailScreen
            isOutlook={isOutlook}
            isGmailSynchronized={isGmailSynchronized}
            isOutlookSynchronized={isOutlookSynchronized}
          />
        </div>
      </div>
    );
  }

  // ── connected but not yet synced ──
  if (!isSynced) {
    return (
      <div className="flex h-full w-full flex-1 flex-col gap-2">
        <div className="mx-auto flex h-full w-full flex-col items-center overflow-hidden ">
          <NoMailScreen
            isOutlook={isOutlook}
            isGmailSynchronized={isGmailSynchronized}
            isOutlookSynchronized={isOutlookSynchronized}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* ── toolbar ── */}
      <div className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-3 py-2">
        {/* inbox / sent pill */}
        <div className="flex items-center gap-1 rounded-lg bg-zinc-800/60 p-0.5">
          {[
            ['Inbox', 'Inbox'],
            ['SentItems', 'Sent'],
          ].map(([key, lbl]) => (
            <button
              key={key}
              onClick={() => handleFilterChange(key, key)}
              className={`rounded-md px-2.5 py-1 text-[10px] font-medium transition-all
                ${emailfilter[key] ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              {lbl}
            </button>
          ))}
        </div>

        {/* filter icon — opens EmailLabels panel */}
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setFilterOpen((p) => !p)}
            className={`rounded-lg p-1.5 transition-colors ${filterOpen ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300'}`}
          >
            <Filter className="h-3.5 w-3.5" />
          </button>

          {filterOpen && (
            <div
              className="animate-fi absolute right-0 top-full z-30 mt-1 rounded-xl border border-zinc-700 bg-zinc-900 p-3 shadow-2xl"
              style={{ minWidth: 210 }}
            >
              <EmailLabels
                emailStats={emailStats}
                emailfilter={emailfilter}
                setEmailFilter={setEmailFilter}
                handleFilterChange={handleFilterChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── list ── */}
      <div className="sc flex-1 overflow-y-auto">
        {loadingEmails ? (
          // skeleton
          <div className="divide-y divide-zinc-800/50">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex animate-pulse items-start gap-3 px-4 py-3"
              >
                <div className="h-7 w-7 shrink-0 rounded-full bg-zinc-800" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-3/4 rounded bg-zinc-800" />
                  <div className="h-2.5 w-full rounded bg-zinc-800/60" />
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
            <FiMail className="h-7 w-7 text-zinc-600" />
            <p className="text-xs text-zinc-500">No emails found</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/50">
            {sorted.map((email, i) => {
              const id = email.id || i;
              const isOpen = expandedId === id;
              const from = email?.from?.name || email?.from || 'Unknown';
              const subject = email?.subject || '(no subject)';
              const snippet = (
                email?.snippet ||
                email?.bodyPreview ||
                ''
              ).slice(0, 80);
              const date = email?.date
                ? new Date(email.date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                  })
                : '';

              return (
                <div key={id}>
                  <button
                    onClick={() => {
                      const newId = id === expandedId ? null : id;
                      setExpandedId(newId);
                      if (newId !== null) fetchDetail(email);
                    }}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors
                      ${isOpen ? 'bg-zinc-800/60' : 'hover:bg-zinc-800/40'}`}
                  >
                    {/* avatar */}
                    <div
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white
                      ${['bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600', 'bg-rose-600', 'bg-cyan-600'][i % 6]}`}
                    >
                      {from[0]?.toUpperCase()}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`truncate text-xs ${!email.isRead || emailfilter['SentItems'] ? 'font-semibold text-zinc-100' : 'text-zinc-300'}`}
                        >
                          {from.length > 20 ? from.slice(0, 20) + '…' : from}
                        </p>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {email.priority !== undefined && (
                            <PriorityBadge priority={email.priority} />
                          )}
                          <span className="text-[10px] text-zinc-600">
                            {date}
                          </span>
                        </div>
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-zinc-400">
                        {subject}
                      </p>
                      {!isOpen && (
                        <p className="mt-0.5 truncate text-[10px] text-zinc-600">
                          {snippet}
                        </p>
                      )}
                      {/* Sentiment + category labels in header */}
                      {(email.sentiment ||
                        (email.objectiveClass &&
                          email.objectiveClass.length > 0)) && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {email.sentiment && (
                            <span className="rounded-full border border-zinc-600 bg-zinc-700 px-1.5 py-0.5 text-[9px] font-medium text-zinc-300">
                              {email.sentiment}
                            </span>
                          )}
                          {(Array.isArray(email.objectiveClass)
                            ? email.objectiveClass
                            : email.objectiveClass
                              ? [email.objectiveClass]
                              : []
                          ).map((tag, ti) => (
                            <span
                              key={ti}
                              className="rounded-full border border-[#5652CF]/30 bg-[#5652CF]/20 px-1.5 py-0.5 text-[9px] font-medium text-[#9b98f5]"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <FiChevronDown
                      className={`mt-1 h-3 w-3 shrink-0 text-zinc-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isOpen &&
                    (() => {
                      const msgId = email?.message_id || email?.id;
                      const rich = (msgId && detailCache[msgId]) || email;
                      return (
                        <EmailDetail
                          email={rich}
                          onClose={() => setExpandedId(null)}
                          onChatWithAgent={handleChatWithAgent}
                        />
                      );
                    })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── pagination ── */}
      {totalPages > 1 && (
        <div className="flex shrink-0 items-center justify-between border-t border-zinc-800 px-4 py-2">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className="rounded p-1 text-zinc-500 transition-colors hover:text-zinc-300 disabled:opacity-30"
          >
            <FiChevronDown className="h-3.5 w-3.5 rotate-90" />
          </button>
          <span className="text-[10px] font-medium text-zinc-500">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={nextPage}
            disabled={currentPage === totalPages}
            className="rounded p-1 text-zinc-500 transition-colors hover:text-zinc-300 disabled:opacity-30"
          >
            <FiChevronDown className="h-3.5 w-3.5 -rotate-90" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Meetings Panel ───────────────────────────────────────────────────────────

function MeetingsPanel() {
  const [expanded, setExpanded] = useState(null);
  const [prepMeeting, setPrepMeeting] = useState(null);
  return (
    <div className="sc relative flex-1 overflow-y-auto">
      <div className="divide-y divide-zinc-800/50">
        {MEETINGS.map((m) => (
          <div key={m.id}>
            <button
              onClick={() => setExpanded((p) => (p === m.id ? null : m.id))}
              className="w-full px-4 py-3 text-left transition-colors hover:bg-zinc-800/60"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2.5">
                  <FiVideo className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500" />
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium leading-snug text-zinc-200">
                      {m.title}
                    </p>
                    <p className="mt-0.5 text-[10px] text-zinc-500">{m.time}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <div className="flex -space-x-1.5">
                    {m.attendees.slice(0, 3).map((a, i) => (
                      <Av key={i} initials={a} color={m.colors[i]} />
                    ))}
                    {m.attendees.length > 3 && (
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-700 text-[9px] font-semibold text-zinc-300">
                        +{m.attendees.length - 3}
                      </span>
                    )}
                  </div>
                  <FiChevronDown
                    className={`h-3.5 w-3.5 text-zinc-500 transition-transform duration-200 ${expanded === m.id ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>
            </button>
            {expanded === m.id && (
              <div className="animate-fi space-y-3 px-4 pb-4">
                {m.summary && (
                  <div className="rounded-lg bg-zinc-800/60 p-3">
                    <Label>Meeting Summary</Label>
                    <p className="mt-1.5 text-xs leading-relaxed text-zinc-300">
                      {m.summary}
                    </p>
                  </div>
                )}
                {m.prep && (
                  <div className="flex items-center justify-between">
                    <Label>Meeting Prep</Label>
                    <button
                      onClick={() => setPrepMeeting(m)}
                      className="rounded-full border border-zinc-600 px-3 py-1 text-[10px] font-medium text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                    >
                      Open
                    </button>
                  </div>
                )}
                {m.emails && (
                  <div className="space-y-1.5">
                    {m.emails.map((e, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Av
                          initials={m.attendees[i] || '??'}
                          color={m.colors[i] || 'bg-zinc-600'}
                        />
                        <span className="truncate text-[11px] text-zinc-400">
                          {e}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {prepMeeting && (
        <PrepModal meeting={prepMeeting} onClose={() => setPrepMeeting(null)} />
      )}
    </div>
  );
}

// ─── CR Todo Item ─────────────────────────────────────────────────────────────

const CR_SOURCE_STYLE = {
  email: {
    label: 'Email',
    cls: 'bg-blue-500/20 text-blue-400 border border-blue-500/20',
  },
  chat: {
    label: 'Chat',
    cls: 'bg-green-500/20 text-green-400 border border-green-500/20',
  },
  meeting: {
    label: 'Meeting',
    cls: 'bg-purple-500/20 text-purple-400 border border-purple-500/20',
  },
};

function CRTodoItem({ cr, onApprove, onReject, onOpen }) {
  const src = CR_SOURCE_STYLE[cr.source_origin] || {
    label: cr.source_origin || 'Unknown',
    cls: 'bg-zinc-700 text-zinc-400',
  };
  return (
    <div className="px-3 py-2.5">
      <div
        className="rounded-xl border border-amber-500/25 bg-amber-500/5 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.08)]"
        style={{ borderLeft: '3px solid rgba(245,158,11,0.7)' }}
      >
        <div className="px-3 pb-2 pt-3">
          <div className="mb-1.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[10px] font-bold text-amber-400">
                {cr.id}
              </span>
              <span
                className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${src.cls}`}
              >
                {src.label}
              </span>
            </div>
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-amber-400">
              Change Request
            </span>
          </div>
          <button
            onClick={onOpen}
            className="mb-0.5 text-left text-xs font-semibold leading-snug text-zinc-100 transition-colors hover:text-white"
          >
            {cr.title}
          </button>
          <p className="mb-2.5 text-[11px] text-zinc-500">
            {cr.raisedBy} · {cr.date}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onOpen}
              className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] font-semibold text-amber-300 transition-colors hover:bg-amber-500/20"
            >
              View
            </button>
            <button
              onClick={onApprove}
              className="flex-1 rounded-lg border border-emerald-600/40 bg-emerald-600/20 py-1.5 text-[11px] font-semibold text-emerald-400 transition-colors hover:bg-emerald-600/30"
            >
              Approve
            </button>
            <button
              onClick={onReject}
              className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-[11px] font-semibold text-red-400 transition-colors hover:bg-red-500/20"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Todo Panel ───────────────────────────────────────────────────────────────

function TodoPanel({ compact = false, onCrApproved }) {
  const { pendingCRs, approveChangeRequest, rejectChangeRequest, openCRModal } =
    useChangeRequests();
  const [todos, setTodos] = useState(TODOS_INIT);
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newNote, setNewNote] = useState('');

  const toggle = (id) =>
    setTodos((p) => p.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
  const addSug = (s) =>
    setTodos((p) => [
      ...p,
      { id: Date.now(), text: s.text, done: false, note: '' },
    ]);
  const addNew = () => {
    if (newText.trim()) {
      setTodos((p) => [
        ...p,
        {
          id: Date.now(),
          text: newText.trim(),
          done: false,
          note: newNote.trim(),
        },
      ]);
      setNewText('');
      setNewNote('');
      setIsAdding(false);
    }
  };
  const cancelAdding = () => {
    setIsAdding(false);
    setNewText('');
    setNewNote('');
  };

  const handleApproveCR = (cr) => {
    approveChangeRequest(cr.id);
    onCrApproved?.(cr);
  };

  return (
    <div className="sc flex h-full min-h-0 flex-col overflow-y-auto">
      {/* Pending Change Requests */}
      {pendingCRs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 border-b border-zinc-800/50 px-4 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-400/70">
              Pending Approval
            </span>
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-black">
              {pendingCRs.length}
            </span>
          </div>
          {pendingCRs.map((cr) => (
            <CRTodoItem
              key={cr.id}
              cr={cr}
              onOpen={() => openCRModal(cr)}
              onApprove={() => handleApproveCR(cr)}
              onReject={() => rejectChangeRequest(cr.id)}
            />
          ))}
        </div>
      )}

      {/* Todos */}
      <div className="flex flex-col divide-y divide-zinc-800/50">
        {todos.map((t) => (
          <div
            key={t.id}
            className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-zinc-800/60"
          >
            <button
              onClick={() => toggle(t.id)}
              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-all
                ${t.done ? 'border-transparent bg-white/20 text-white' : 'border-zinc-500 hover:border-zinc-300'}`}
            >
              {t.done && <FiCheck className="h-3 w-3" strokeWidth={3} />}
            </button>
            <div className="min-w-0 flex-1">
              <p
                className={`truncate text-xs font-medium ${t.done ? 'text-zinc-500 line-through' : 'text-zinc-200'}`}
              >
                {t.text}
              </p>
              {!compact && t.note && (
                <p className="mt-0.5 line-clamp-2 text-[11px] text-zinc-400">
                  {t.note}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add new todo */}
      <div className="flex items-start gap-3 border-b border-zinc-800/50 px-4 py-3">
        <div className="mt-0.5 h-4 w-4 shrink-0 rounded border border-zinc-600 opacity-50" />
        <div className="flex min-w-0 flex-1 flex-col">
          <input
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onFocus={() => setIsAdding(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addNew();
              if (e.key === 'Escape') cancelAdding();
            }}
            placeholder="Add new to do"
            className="w-full bg-transparent text-xs font-medium text-zinc-200 placeholder-zinc-500 outline-none"
          />
          {isAdding && (
            <div className="mt-1 flex flex-col">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    addNew();
                  }
                  if (e.key === 'Escape') cancelAdding();
                }}
                placeholder="Notes"
                className="mt-1 h-12 w-full resize-none bg-transparent text-[11px] text-zinc-400 placeholder-zinc-600 outline-none"
              />
              <div className="mt-2 flex items-center justify-between border-t border-zinc-800/50 pt-2">
                <button className="rounded bg-zinc-800 p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700">
                  <VscReport className="h-3.5 w-3.5" />
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={cancelAdding}
                    className="px-2 py-1 text-[11px] text-zinc-500 hover:text-zinc-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addNew}
                    className="rounded bg-zinc-800 px-3 py-1 text-[11px] text-zinc-200 transition-colors hover:bg-zinc-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Suggestions */}
      {!compact && (
        <div className="mb-4 mt-6 px-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-70"
              >
                <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M2 15h10" />
                <path d="m9 18 3-3-3-3" />
              </svg>
              SUGGESTIONS
            </span>
          </div>
          <div className="flex flex-col">
            {SUGGESTIONS.map((s) => (
              // eslint-disable-next-line
              <div
                key={s.id}
                className="group flex cursor-pointer items-start gap-3 py-2"
                onClick={() => addSug(s)}
              >
                <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                  <FiPlus className="h-3.5 w-3.5 text-zinc-600 transition-colors group-hover:text-zinc-300" />
                </div>
                <p className="text-xs font-medium leading-snug text-zinc-400 transition-colors group-hover:text-zinc-200">
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Morning Briefing view
// ─────────────────────────────────────────────────────────────────────────────

function MorningBriefingView({ onClose, onStageChat }) {
  const [activeTab, setActiveTab] = useState('emails');
  const briefingTabs = {
    emails: { icon: <FiMail className="h-3.5 w-3.5" />, label: 'Emails' },
    meetings: { icon: <FiVideo className="h-3.5 w-3.5" />, label: 'Meetings' },
  };

  return (
    <div className="bg-1 animate-si flex h-full w-full overflow-hidden rounded-xl border border-zinc-700">
      {/* LEFT: greeting + todo */}
      <div
        className="relative flex flex-col overflow-hidden border-r border-zinc-800"
        style={{
          width: '60%',
          minWidth: 0,
          background:
            'linear-gradient(150deg, #18181b 0%, #27272a 55%, #1e1b4b 100%)',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 z-0 opacity-[0.15]"
          style={{
            background:
              'radial-gradient(ellipse at 15% 65%, #6366f1 0%, transparent 60%)',
          }}
        />

        {/* hero */}
        <div className="relative z-10 shrink-0 px-7 pb-7 pt-9">
          <p className="mb-3 text-[11px] font-medium tracking-wide text-zinc-500">
            MARCH 29TH, 2026 &nbsp;·&nbsp; MATHURA 35°
          </p>
          <h1 className="text-[1.6rem] font-semibold leading-[1.3] text-zinc-100">
            Good morning, <span className="font-bold text-white">Akshay.</span>
          </h1>
          <p className="mt-0.5 text-lg font-normal leading-snug text-zinc-300">
            Clear Sunday,
            <br />
            <span className="text-base font-light text-zinc-400">
              one thing carrying over.
            </span>
          </p>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 rounded-xl p-2 text-zinc-500 transition-colors hover:bg-zinc-800/80 hover:text-zinc-200"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {/* briefing text */}
        <div className="relative z-10 shrink-0 border-b border-zinc-800 px-6 py-4">
          <div className="mb-2.5 flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              ☀ Morning Briefing
            </span>
          </div>
          <p className="text-xs leading-relaxed text-zinc-300">
            No meetings today. The{' '}
            <span className="inline-flex items-center gap-1 align-middle">
              <span className="inline-block h-3 w-3 shrink-0 rounded-sm bg-gradient-to-br from-blue-500 to-red-500 align-middle" />
              <span className="font-medium text-zinc-100">
                investor meeting thread
              </span>
            </span>{' '}
            from{' '}
            <span className="font-medium text-zinc-100">
              Akshay (akshayjangra225)
            </span>{' '}
            is still unread and marked urgent, asking about timing for the
            Hyderabad trip.
          </p>
          <p className="mt-2 text-xs leading-relaxed text-zinc-500">
            Pleasant 23°C evening in Mathura. Enjoy the rest of your weekend.
          </p>
        </div>

        {/* To Do label */}
        <div className="relative z-10 flex shrink-0 items-center gap-2 border-b border-zinc-800 px-6 py-2.5">
          <FiCheck className="h-3 w-3 text-zinc-500" />
          <Label>To Do</Label>
        </div>

        <div className="relative z-10 min-h-0 flex-1 overflow-hidden">
          <TodoPanel compact />
        </div>
      </div>

      {/* RIGHT: emails / meetings */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-4 pb-3 pt-4">
          <div>
            <p className="text-[10px] text-zinc-500">Catch Up</p>
            <h2 className="text-base font-semibold leading-tight text-zinc-100">
              {briefingTabs[activeTab].label}
            </h2>
          </div>
          <div className="flex items-center gap-1 rounded-xl bg-zinc-800/60 p-1">
            {Object.entries(briefingTabs).map(([key, t]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all
                  ${activeTab === key ? 'bg-zinc-700 text-zinc-100 shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeTab === 'emails' && (
            <EmailPanel
              onStageChat={(...args) => {
                onClose();
                onStageChat?.(...args);
              }}
            />
          )}
          {activeTab === 'meetings' && <MeetingsPanel />}
        </div>
        <div className="shrink-0 border-t border-zinc-800 px-4 py-6">
          <button
            onClick={onClose}
            className="group flex w-full items-center justify-center gap-3 text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-200"
          >
            <span>Finish Morning Briefing</span>
            <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

const RightSideActionTracker = ({
  isMorningBriefing = false,
  onMorningBriefing,
  onStageChat,
}) => {
  // ── Workspace context ──
  const {
    currentToolCallStatus,
    conversations,
    setScrollToToolId,
    creatingTask,
    isReasoning,
  } = DataState();
  const { plan, planStatus } = useTaskPlan();
  const {
    notifications,
    openNotificationInContext,
    setActiveNotificationId,
    removeCollapsedNotificationId,
  } = useNotifications();
  const { openCRModal } = useChangeRequests();
  const navigate = useNavigate();

  // ── Local state ──
  const [showAllGoals, setShowAllGoals] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks'); // tasks | notifications | waitingEvents | todo | meetings | emails

  const [isLastChatToolCalls, setIsLastChatToolCalls] = useState(false);
  useEffect(() => {
    conversations.forEach((c) => {
      if (c?.role === 'assistant') {
        setIsLastChatToolCalls(
          c?.tool_calls &&
            Array.isArray(c?.tool_calls) &&
            c?.tool_calls.length > 0,
        );
      }
    });
  }, [conversations, currentToolCallStatus]);

  const isCurrent = isLastChatToolCalls;
  const successfulTasks = currentToolCallStatus.filter(
    (a) => a.status === 'success',
  ).length;

  // Derive pending waiting items: processed meetings that need Jira review/approval
  const derivedWaitingEvents = notifications.filter(
    (n) =>
      n.source_type === 'meeting' &&
      ['processed', 'reprocessing'].includes(n.transcript_status),
  );

  // Count pending Jira updates across all waiting meetings
  const pendingJiraCount = derivedWaitingEvents.reduce((sum, n) => {
    const tasks = n.llm_summary?.task_wise_summary || [];
    const updates = tasks.reduce(
      (s, t) => s + (t.proposed_jira_issue_updates?.length || 0),
      0,
    );
    const actions = (n.llm_summary?.extracted_action_items || []).length;
    return sum + updates + actions;
  }, 0);

  const handleNotificationClick = (notification) => {
    if (notification.source_type === 'change_request') {
      openCRModal(notification.cr_data);
      return;
    }
    openNotificationInContext(notification.id);
    setActiveNotificationId(notification.id);
    removeCollapsedNotificationId(notification.id);
  };

  // ── Tab definitions ──
  // Workspace group
  const workspaceTabs = {
    tasks: { label: 'Tasks', icon: <ListTodo className="h-3.5 w-3.5" /> },
    notifications: {
      label: 'Notifications',
      icon: <Bell className="h-3.5 w-3.5" />,
    },
    waitingEvents: {
      label: 'Waiting',
      icon: <FiClock className="h-3.5 w-3.5" />,
      count:
        pendingJiraCount > 0 ? pendingJiraCount : derivedWaitingEvents.length,
    },
  };
  // Personal group
  const personalTabs = {
    todo: { label: 'To Do', icon: <FiCheck className="h-3.5 w-3.5" /> },
    meetings: { label: 'Meetings', icon: <FiVideo className="h-3.5 w-3.5" /> },
    emails: { label: 'Emails', icon: <FiMail className="h-3.5 w-3.5" /> },
  };

  const allTabs = { ...workspaceTabs, ...personalTabs };
  const isWorkspaceTab = (key) => key in workspaceTabs;

  // ── Workspace content renderer ──
  const renderWorkspaceContent = () => {
    switch (activeTab) {
      case 'notifications':
        return (
          <div className="sc flex-1 divide-y divide-zinc-800/50 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-6 text-center text-xs italic text-zinc-500">
                No notifications yet.
              </p>
            ) : (
              notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onClick={() => handleNotificationClick(n)}
                />
              ))
            )}
          </div>
        );
      case 'waitingEvents':
        return (
          <div className="sc flex-1 divide-y divide-zinc-800/50 overflow-y-auto">
            {derivedWaitingEvents.length === 0 ? (
              <p className="p-6 text-center text-xs italic text-zinc-500">
                Nothing waiting.
              </p>
            ) : (
              derivedWaitingEvents.map((e) => (
                <WaitingEventItem
                  key={e.id}
                  event={e}
                  onClick={() =>
                    navigate(
                      `/meeting-summary/${e.id}${e.user_provided_meeting ? '?manual=true' : ''}`,
                    )
                  }
                />
              ))
            )}
          </div>
        );
      case 'tasks':
      default:
        if (creatingTask) return <WaitingForTask />;
        return currentToolCallStatus.length > 0 ? (
          <div className="sc flex-1 divide-y divide-zinc-800/50 overflow-y-auto">
            {currentToolCallStatus.map((tool, i) => (
              <ActionItem
                key={i}
                action={tool}
                onActionClick={setScrollToToolId}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-xs italic text-zinc-500">
            No actions initiated yet.
          </div>
        );
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Morning Briefing mode
  // ─────────────────────────────────────────────────────────────────────────
  if (isMorningBriefing) {
    return (
      <>
        <div className="flex h-full w-full flex-col overflow-hidden">
          <MorningBriefingView
            onClose={() => onMorningBriefing?.(false)}
            onStageChat={onStageChat}
          />
        </div>
        <Styles />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Normal sidebar mode
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="bg-1 flex h-full w-full flex-col overflow-hidden rounded-xl border border-zinc-700">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-700 px-4 py-3">
          <div className="min-w-0">
            {/* <p className="text-[10px] text-zinc-500">Mathura 35°</p> */}
            <h1 className="truncate text-lg font-semibold leading-tight tracking-tight text-zinc-100">
              {allTabs[activeTab]?.label ?? 'Workspace'}
            </h1>
          </div>

          {/* Running badge (tasks only) */}
          {activeTab === 'tasks' &&
            !creatingTask &&
            (isCurrent || planStatus !== 'idle') && (
              <div className="ml-2 flex shrink-0 items-center gap-2 rounded-full border border-blue-700/60 bg-blue-900/50 px-2.5 py-1">
                <div className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />
                <span className="text-xs font-medium text-blue-300">
                  Running
                </span>
              </div>
            )}
        </div>

        {/* ── Tab bar: two groups separated by a divider ──────────────────── */}
        <div className="flex shrink-0 items-center gap-0.5 border-b border-zinc-800 px-3 py-2">
          {/* Workspace group */}
          {Object.entries(workspaceTabs).map(([key, t]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              title={t.label}
              className={`relative flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-all
                ${
                  activeTab === key
                    ? 'bg-zinc-700 text-zinc-100'
                    : 'text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-300'
                }`}
            >
              {t.icon}
              {t.count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[8px] font-bold text-black">
                  {t.count > 9 ? '9+' : t.count}
                </span>
              )}
            </button>
          ))}

          {/* Divider */}
          <div className="mx-1.5 h-4 w-px shrink-0 bg-zinc-700" />

          {/* Personal group */}
          {Object.entries(personalTabs).map(([key, t]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              title={t.label}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-all
                ${
                  activeTab === key
                    ? 'bg-zinc-700 text-zinc-100'
                    : 'text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-300'
                }`}
            >
              {t.icon}
              {/* <span className="hidden lg:inline">{t.label}</span> */}
            </button>
          ))}
        </div>

        {/* ── Task Plan (workspace: tasks tab only) ───────────────────────── */}
        {activeTab === 'tasks' &&
          !creatingTask &&
          isReasoning &&
          plan &&
          plan.length > 0 && (
            <div className="shrink-0">
              <div className="flex items-center justify-between border-b border-zinc-700 px-4 py-2">
                <div className="flex items-center py-1">
                  {planStatus !== 'idle' && (
                    <div className="mr-4 flex items-center space-x-1.5">
                      {[0, 0.3, 0.6].map((delay, i) => (
                        <div
                          key={i}
                          className="h-2 w-2 animate-pulse rounded-full bg-blue-400"
                          style={{ animationDelay: `${delay}s` }}
                        />
                      ))}
                    </div>
                  )}
                  <h3 className="text-xs font-medium uppercase tracking-wider text-zinc-400">
                    {planStatus === 'planning'
                      ? 'Planning'
                      : planStatus === 'analyzing'
                        ? 'Analyzing'
                        : 'Plan'}
                  </h3>
                </div>
                {plan.length > RECENT_GOAL_COUNT && (
                  <button
                    onClick={() => setShowAllGoals((p) => !p)}
                    title={showAllGoals ? 'Show recent' : 'Show all'}
                    className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
                  >
                    {showAllGoals ? (
                      <ChevronUp className="h-3 w-4" />
                    ) : (
                      <Ellipsis className="h-3 w-4" />
                    )}
                  </button>
                )}
              </div>
              <TaskPlan showAll={showAllGoals} />
            </div>
          )}

        {/* ── Content area ────────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Workspace tabs */}
          {isWorkspaceTab(activeTab) && renderWorkspaceContent()}

          {/* Personal tabs */}
          {activeTab === 'todo' && (
            <TodoPanel onCrApproved={() => setActiveTab('notifications')} />
          )}
          {activeTab === 'meetings' && <MeetingsPanel />}
          {activeTab === 'emails' && <EmailPanel onStageChat={onStageChat} />}
        </div>

        {/* ── Footer bar (tasks count + morning briefing) ──────────────────── */}
        <div className="shrink-0 border-t border-zinc-800">
          {activeTab === 'tasks' &&
            !creatingTask &&
            currentToolCallStatus.length > 0 && (
              <div className="flex justify-between border-b border-zinc-800 bg-zinc-900/40 px-4 py-2 text-[11px] text-zinc-500">
                <span>{successfulTasks} completed</span>
                <span>{currentToolCallStatus.length} total</span>
              </div>
            )}
          <div className="px-4 py-3">
            <button
              onClick={() => onMorningBriefing?.(true)}
              className="group flex w-full items-center justify-center gap-3 text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-200"
            >
              <span>Morning Briefing</span>
              <FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>

      <Styles />
    </>
  );
};

function Styles() {
  return (
    <style>{`
      .sc::-webkit-scrollbar { width: 4px; }
      .sc::-webkit-scrollbar-track { background: transparent; }
      .sc::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 4px; }
      @keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
      .animate-fi { animation: fadeIn .15s ease-out; }
      @keyframes slideIn { from { opacity:0; transform:translateX(10px); } to { opacity:1; transform:translateX(0); } }
      .animate-si { animation: slideIn .2s ease-out; }
    `}</style>
  );
}

export default RightSideActionTracker;
